import { FC, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { intersection } from "lodash";
import styled from "styled-components";

import { Dropdown, Form } from "capitalrx-components-2.0/lib/forms";
import { Icon, Modal } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { Drug, PricesRejectInfo, SubmissionClarificationCode } from "@/App/api/drug";
import { ThemedButton } from "@/App/components";
import { nonSuppressableRejectCodes, rejectReason, suppressableRejectCodes } from "@/App/constants/constants";
import { formatRejectMessage } from "@/App/utils/utils";

import { isACAHIVPrEPRejectionCheck, isRxCapProgramCheck } from "./utils";

const LargeIcon = styled(Icon)`
  min-height: unset !important;
  max-height: unset !important;
  min-width: unset !important;
  max-width: unset !important;
  width: 32px;
  height: 32px;
`;

interface FindBestPriceRejectModalProps {
  readonly isVisible: boolean;
  readonly rejectInfo: PricesRejectInfo;
  readonly hasShownCapRxMessage: boolean;
  readonly rejectCodes: string[];
  readonly programs: string[];
  readonly drug: Drug;
  readonly onDismiss: (suppressCodes: string[], submissionClarificationCode: null | SubmissionClarificationCode) => void;
}

export const FindBestPriceRejectModal: FC<FindBestPriceRejectModalProps> = ({
  isVisible,
  rejectInfo,
  rejectCodes,
  hasShownCapRxMessage,
  programs,
  drug,
  onDismiss,
}) => {
  const { t: translate } = useTranslation();
  const isNotSuppressable = useMemo(() => {
    const allCodes = [rejectInfo.reject_code, ...rejectCodes];
    // in the case when there's both a PA and Product Not Covered reject, don't allow them to suppress the 70.
    const has70And75 = allCodes.includes(rejectReason.productServiceNotCovered) && allCodes.includes(rejectReason.priorAuthRequired);
    return intersection(nonSuppressableRejectCodes, allCodes).length > 0 || has70And75;
  }, [rejectCodes, rejectInfo]);
  const submissionClarificationCodeForm = useForm<SubmissionClarificationCode>();
  const { isACAHIVPrEPRejection, submissionClarificationCodeProgram } = useMemo(() => {
    const [isACAHIVPrEP, acaHIVPrEPProgram] = isACAHIVPrEPRejectionCheck(rejectCodes, programs);
    if (isACAHIVPrEP) {
      return {
        isACAHIVPrEPRejection: true,
        submissionClarificationCodeProgram: acaHIVPrEPProgram,
      };
    }
    return {};
  }, [rejectCodes, programs]);

  const showDialogIcon = !isACAHIVPrEPRejection;
  const hasRxCapProgram = isRxCapProgramCheck(programs);

  const modalTitle = useMemo(() => {
    const allCodes = [rejectInfo.reject_code, ...rejectCodes];

    if (isACAHIVPrEPRejection) {
      return translate("FIND_BEST_PRICE.findBestPriceRejectModal.provideAdditionalDrugInformation");
    } else if (hasRxCapProgram) {
      return translate("FIND_BEST_PRICE.findBestPriceRejectModal.rxCapProgramNotice");
    } else if (isNotSuppressable) {
      if (rejectInfo.rejected_by_all_pharmacies) {
        return translate("FIND_BEST_PRICE.findBestPriceRejectModal.ourPriceRequestHasBeenRejectedByAllPharmacies");
      } else {
        return translate("FIND_BEST_PRICE.findBestPriceRejectModal.ourPriceRequestHasBeenRejectedBySomePharmacies");
      }
    } else {
      if (allCodes.includes(rejectReason.priorAuthRequired)) {
        return `${drug.drug_name} ${translate("FIND_BEST_PRICE.findBestPriceRejectModal.requiresPriorAuthorization")}`;
      } else if (allCodes.includes(rejectReason.notCovered)) {
        return `${drug.drug_name} ${translate("FIND_BEST_PRICE.findBestPriceRejectModal.isNotCovered")}`;
      } else {
        return translate("FIND_BEST_PRICE.findBestPriceRejectModal.claimRejected");
      }
    }
  }, [isNotSuppressable, drug, rejectInfo, rejectCodes, isACAHIVPrEPRejection]);

  const modalContent = useMemo(() => {
    const allCodes = [rejectInfo.reject_code, ...rejectCodes];
    if (!drug) {
      return "";
    }
    if (isACAHIVPrEPRejection) {
      return (
        <Form
          form={submissionClarificationCodeForm}
          onSubmit={() => onSubmitSubmissionClarificationCode()}
          testId="submission-clarification-code-form"
        >
          <Stack>
            <Paragraph>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.thisDrugRequiresAdditionalInformation")}</Paragraph>
            <Grid>
              <Grid.Span columns={6}>
                <Dropdown.Field
                  testId="submission-clarification-code"
                  required
                  label={translate("FIND_BEST_PRICE.findBestPriceRejectModal.drugCategory")}
                  name="submission_clarification_code"
                  validator={(value) => !!value}
                >
                  <Dropdown.Option value={"10"}>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.hivPrep")}</Dropdown.Option>
                  <Dropdown.Option value={"01"}>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.hivNonPrep")}</Dropdown.Option>
                </Dropdown.Field>
              </Grid.Span>
            </Grid>
          </Stack>
        </Form>
      );
    } else if (hasRxCapProgram && !hasShownCapRxMessage) {
        return (
          <Paragraph testId="rx-cap-message">
            <Trans
              i18nKey={"FIND_BEST_PRICE.findBestPriceRejectModal.rxCapProgramMessage"}
              components={{ b: <b /> }}
            />
          </Paragraph>
        );
    } else if (isNotSuppressable) {
      return (
        <Stack spacing="mini">
          {rejectInfo.reject_message_list.length === 1 && (
            <Paragraph>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.reasonForRejection")}</Paragraph>
          )}
          {rejectInfo.reject_message_list.length > 1 && (
            <Paragraph>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.reasonsForRejection")}</Paragraph>
          )}
          <ul>
            {rejectInfo.reject_message_list.map((reject, idx) => {
              return (
                <li data-testid={`reject-message-${idx}`} key={reject}>
                  {reject}
                </li>
              );
            })}
          </ul>
        </Stack>
      );
    } else {
      if (allCodes.includes(rejectReason.priorAuthRequired)) {
        return (
          <Paragraph testId="prior-auth-warning-message">
            <Trans
              i18nKey="FIND_BEST_PRICE.findBestPriceRejectModal.drugRequiresaPriorAuthorization"
              values={{
                drugName: drug.drug_name,
              }}
            />
          </Paragraph>
        );
      } else if (allCodes.includes(rejectReason.notCovered)) {
        return (
          <Paragraph testId="not-covered-warning-message">
            {drug.drug_name} {translate("FIND_BEST_PRICE.findBestPriceRejectModal.drugIsNotCoveredByYourPlan")}
          </Paragraph>
        );
      } else {
        return (
          <Stack spacing="mini">
            <Paragraph testId="other-rejects-warning-message">
              <Trans
                i18nKey="FIND_BEST_PRICE.findBestPriceRejectModal.yourRequestForDrugWasRejected"
                values={{
                  drugName: drug.drug_name,
                }}
              />
            </Paragraph>
            <ul>
              <li>{formatRejectMessage(rejectInfo.reject_message || "")}</li>
            </ul>
            <Paragraph>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.youMayContinueToViewPrices")}</Paragraph>
          </Stack>
        );
      }
    }
  }, [isNotSuppressable, drug, rejectInfo, rejectCodes, isACAHIVPrEPRejection, hasRxCapProgram]);

  const onClose = useCallback(() => {
    if (hasRxCapProgram && !hasShownCapRxMessage) {
      onDismiss([], null);
    } else if (!isNotSuppressable) {
      const allCodes = [rejectInfo.reject_code, ...rejectCodes];
      onDismiss(intersection(allCodes, suppressableRejectCodes), null);
    } else {
      onDismiss([], null);
    }
  }, [isNotSuppressable, rejectInfo, onDismiss, rejectCodes, isACAHIVPrEPRejection, hasRxCapProgram, hasShownCapRxMessage]);

  const onSubmitSubmissionClarificationCode = useCallback(async () => {
    const submission_clarification_code = submissionClarificationCodeForm.getValues().submission_clarification_code;
    if (submission_clarification_code) {
      onDismiss([], {
        program: submissionClarificationCodeProgram,
        submission_clarification_code,
      });
    } else {
      submissionClarificationCodeForm.setError("submission_clarification_code", {
        message: translate("FIND_BEST_PRICE.findBestPriceRejectModal.pleaseSelectAnOption"),
      });
    }
  }, [onDismiss, submissionClarificationCodeProgram, submissionClarificationCodeForm]);

  const modalFooter = useMemo(() => {
    if (isACAHIVPrEPRejection) {
      return (
        <Shelf spacing="small">
          <Shelf.Spacer />
          <ThemedButton onClick={onClose} appearance="secondary">
            {translate("FIND_BEST_PRICE.findBestPriceRejectModal.cancel")}
          </ThemedButton>
          <ThemedButton onClick={onSubmitSubmissionClarificationCode}>
            {translate("FIND_BEST_PRICE.findBestPriceRejectModal.confirm")}
          </ThemedButton>
        </Shelf>
      );
    } else if (isNotSuppressable) {
      return (
        <Shelf>
          <Shelf.Spacer />
          <ThemedButton onClick={onClose}>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.close")}</ThemedButton>
        </Shelf>
      );
    } else {
      return (
        <Shelf>
          <Shelf.Spacer />
          <ThemedButton onClick={onClose}>{translate("FIND_BEST_PRICE.findBestPriceRejectModal.continueToPricing")}</ThemedButton>
        </Shelf>
      );
    }
  }, [isNotSuppressable, onClose, isACAHIVPrEPRejection]);

  return (
    <Modal visible={isVisible} title={modalTitle} onClose={onClose} footer={modalFooter}>
      <Stack>
        {showDialogIcon && (
          <Shelf>
            <Shelf.Spacer />
            <LargeIcon color={isNotSuppressable ? "red" : "black"} name={isNotSuppressable ? "x-circle" : "info-circle"} />
            <Shelf.Spacer />
          </Shelf>
        )}
        {modalContent}
      </Stack>
    </Modal>
  );
};
